// bflowPostCall.js
// ─────────────────────────────────────────────────────────────────────────
// A/B variant B ka post-call gate.
//
// /agent-live pe user "Call Sophia Now" dabata hai aur apne PHONE se agent ko
// call karta hai. Us call ke baad usse do me se ek screen dikhani hai:
//
//   call >= 30 sec  → /call-summary   (Sophia ne kya capture kiya + transcript)
//   call <  30 sec  → /21-day-trial   (short call — abhi value dikhi hi nahi)
//
// Dikkat ye hai ki ccxcxc APP KE BAHAR hoti hai. User app ko background me daal
// sakta hai ya KILL bhi kar sakta hai. Isliye "maine dial kiya tha" ka nishaan
// localStorage me rakhte hain (sessionStorage app kill pe udd jaata) aur wapas
// aane par wahin cccccse check dobara chalta hai.
//
// Call ka data /callHistory/agentCalLHistory/:agentId/last3months se aata hai —
// wo newest-first sorted hai aur usme duration_ms hota hai.
// ─────────────────────────────────────────────────────────────────────────

// Isse lamba call = "asli baat hui" — user ne sach me agent ko azmaya.
export const QUALIFYING_CALL_MS = 15000;

// Gate itni der tak armed rehta hai. Uske baad maan lete hain ki user ne call
// ki hi nahi.
//
// Risk jo yahan tola gaya hai: armed rehte hue agar koi ASLI customer call kar
// de to gate uspe fire ho jayega. Practically ye hota nahi — number abhi-abhi
// assign hua hai aur kahin publish nahi hua, to pehle 15 minute me uspe sirf
// khud owner hi call karta hai.
export const PENDING_DIAL_TTL_MS = 15 * 60 * 1000; // 15 min

const PENDING_KEY = "bflow_pending_dial";

const read = () => {
    try {
        const raw = localStorage.getItem(PENDING_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const write = (obj) => {
    try {
        localStorage.setItem(PENDING_KEY, JSON.stringify(obj));
    } catch {
        /* storage blocked — gate bas chalega nahi, koi crash nahi */
    }
};

export const clearPendingDial = () => {
    try {
        localStorage.removeItem(PENDING_KEY);
    } catch {
        /* ignore */
    }
};

/**
 * Pending dial jo abhi bhi valid hai (TTL ke andar). TTL nikal chuka ho to
 * nishaan saaf karke null lauta dete hain, taaki wo hamesha ke liye na baithe.
 */
export const getPendingDial = () => {
    const p = read();
    if (!p?.agentId || !p?.dialedAt) return null;
    if (Date.now() - p.dialedAt > PENDING_DIAL_TTL_MS) {
        clearPendingDial();
        return null;
    }
    return p;
};

/**
 * Gate ko ARM karo — /agent-live pe pahunchte hi, button tap pe NAHI.
 *
 * Ye sabse ahem baat hai: user "Call Sophia Now" dabaye, ye zaroori NAHI. Wo
 * screen pe likha number padh kar apne phone ke dialer se bhi laga sakta hai —
 * bahut log yahi karte hain. Agar hum sirf button tap pe arm karte, to un sab
 * users ka gate kabhi chalta hi nahi aur wo /agent-live pe hamesha ke liye
 * atke reh jaate, chaahe unhone 5 minute lambi call kyun na ki ho.
 *
 * dialedAt ko HAM stamp karte hain (server ka time nahi), aur isi se aage
 * "is se pehle wali purani calls" filter hoti hain — warna onboarding ka
 * purana web-call bhi match kar jaata.
 *
 * Pehle se armed ho to CHHED-CHHAD nahi karte: har re-mount pe timestamp aage
 * khiskane se wo call chhoot sakti hai jo abhi-abhi shuru hui thi.
 */
export const armPostCallGate = (agentId) => {
    if (!agentId) return;
    if (getPendingDial()?.agentId === agentId) return; // pehle se armed
    write({ agentId, dialedAt: Date.now(), dialerOpened: false, returns: 0 });
};

/**
 * User ne dialer khola ("Call X Now" / "Try again").
 *
 * Sirf ISI ke baad app-kill par usse wapas /agent-live pe laaya jaata hai. Jo
 * user screen dekh kar bina dial kiye nikal gaya, use wapas kheenchna galat
 * hai — usne is flow me kuch shuru kiya hi nahi.
 */
export const markDialerOpened = (agentId) => {
    const p = getPendingDial();
    if (!p || (agentId && p.agentId !== agentId)) return;
    if (p.dialerOpened) return;
    write({ ...p, dialerOpened: true });
};

/**
 * App cold-boot par: kya user ko wapas /agent-live pe le jaana chahiye?
 *
 * Lautata hai agentId (le jao) ya null (mat le jao).
 *
 * Rule — ek hi "doosra mauka":
 *   pehla boot  → returns 0→1, agentId lautao (wapas le jao)
 *   doosra boot → marker clear, null (ab dashboard hi sahi)
 *
 * NOTE: "app kill hua" hum detect kar hi nahi sakte — jo dikhta hai wo cold
 * boot hai. Browser refresh bhi wahi cold boot hai, isliye refresh bhi ek
 * return gina jaata hai. Ye jaan-boojh ke maana gaya trade-off hai.
 */
export const consumeReturnSlot = () => {
    const p = getPendingDial();
    if (!p) return null;
    if (!p.dialerOpened) return null; // dialer khola hi nahi tha
    if ((p.returns || 0) >= 1) {
        clearPendingDial(); // doosri baar — flow chhod do
        return null;
    }
    write({ ...p, returns: (p.returns || 0) + 1 });
    return p.agentId;
};

/**
 * Kya YE call wahi hai jiska hum intezaar kar rahe hain?
 *
 * DO shart, dono zaroori:
 *
 *  1. call_type === "phone_call". Hum asli PHONE call ka intezaar kar rahe hain
 *     jo user apne handset se karta hai. Onboarding ka web call (call_type
 *     "web_call") isi agent pe, isi waqt ke aas-paas hota hai — bina is filter
 *     ke wahi match ho jaata aur user ko galat screen mil jaati.
 *
 *  2. start_timestamp dial ke baad ka ho. start_timestamp Retell se
 *     milliseconds me aata hai, par kabhi ISO string bhi ho sakta hai — dono
 *     handle karte hain. 5s ka margin isliye ki user ka device clock aur Retell
 *     ka timestamp thoda alag ho sakta hai.
 */
export const isCallAfterDial = (call, dialedAt) => {
    if (call?.call_type !== "phone_call") return false;
    const ts = Number(call?.start_timestamp) || Date.parse(call?.start_timestamp);
    return Number.isFinite(ts) && ts >= dialedAt - 5000;
};

/**
 * Calls ki list me se wahi call dhoondo — sirf APP-KILL wale case ke liye.
 *
 * Normal case me socket ka phone_call_completed event kaam kar deta hai. Par
 * agar user app hi band kar de, to wo event kisi tak pahunchta hi nahi. Wapas
 * aane par ek BAAR ye lookup chalta hai (polling nahi) taaki chhoota hua call
 * pakda ja sake.
 */
export const findCallAfter = (calls, dialedAt) => {
    if (!Array.isArray(calls)) return null;
    return calls.find((c) => isCallAfterDial(c, dialedAt)) || null;
};

/**
 * Us call ke liye agli screen ka path.
 * 30s+ → call summary, warna trial offer.
 */
export const routeForCall = (call) =>
    Number(call?.duration_ms || 0) >= QUALIFYING_CALL_MS
        ? "/call-summary"
        : "/21-day-trial";
